import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lobby from "./pages/Lobby";
import Game from "./pages/Game";
import Leaderboard from "./pages/Leaderboard";
import OnlineRoom from "./pages/OnlineRoom";
import OnlineGame from "./pages/OnlineGame";
import AuthCallback from "./pages/AuthCallback";
import Store from "./pages/Store";
import Tournaments from "./pages/Tournaments";
import Profile from "./pages/Profile";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import DeleteAccount from "./pages/DeleteAccount";
import OfflineToast from "./components/OfflineToast";
import { fetchOrCreateProfile, getStoredName } from "./lib/api";
import { rehydrateNativeSession, initGoogleAuthNative } from "./lib/authNative";
import { setupAxiosRetry } from "./lib/network";

// Install the global axios retry interceptor exactly once at module load.
setupAxiosRetry();
// (rehydrate moved into the useEffect below so we can `await` it — the
// rehydrate now reads from @capacitor/preferences when localStorage is empty,
// which is async. Awaiting it before fetching the profile prevents the lobby
// from racing ahead and using a stale anon uid.)

function App() {
  useEffect(() => {
    (async () => {
      try {
        // 1. Restore session_token + uid (from Preferences if localStorage was wiped)
        await rehydrateNativeSession();
      } catch (e) { console.warn("rehydrate failed:", e); }
      // 2. NOW the profile fetch sees the correct uid + Authorization Bearer
      fetchOrCreateProfile(getStoredName()).catch((e) => console.error(e));
    })();
    // Push notifications — no-op on web, full FCM register on Android.
    import("./lib/push").then(({ initPushNotifications }) => {
      initPushNotifications().catch((e) => console.warn("push init:", e));
    }).catch(() => {});
    // Initialize the native Google Sign-In plugin (no-op on web).
    initGoogleAuthNative().catch(() => {});
    // Initialize Google Play Billing (no-op on web). Without this kick-off, the
    // Store page's whenPlayBillingReady() would hang until its 6s timeout.
    import("./lib/playBilling").then(({ initPlayBilling }) => {
      initPlayBilling().catch((e) => console.warn("play billing init:", e));
    }).catch(() => {});
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <OfflineToast />
        <Routes>
          <Route path="/" element={<Lobby />} />
          <Route path="/play" element={<Game />} />
          <Route path="/leaderboard" element={<Leaderboard />} />
          <Route path="/online/new" element={<OnlineRoom mode="create" />} />
          <Route path="/online/join" element={<OnlineRoom mode="join" />} />
          <Route path="/online/room/:code" element={<OnlineRoom mode="join" />} />
          <Route path="/online/play/:code" element={<OnlineGame />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/store" element={<Store />} />
          <Route path="/tournaments" element={<Tournaments />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
